import { Spinner, useToast } from "@chakra-ui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  useSubmitContributingEntityResponseMutation,
  useUpdateContributingEntityCommitmentMutation,
  useUpdateContributingEntityQualificationResponsesMutation,
  useUpdateContributingEntityResponseMutation,
  type ContributingEntityCommitmentDto,
  type ContributingEntityDetailDto,
  type ContributingEntityQualificationResponseDto,
  type EspdResponseDto,
} from "../../../../autogen/bff-api";
import { useApiError } from "../../../../common/errors/useApiError";
import { emptyEspd } from "../bid/espd/useEspdAnswers";
import { ContributingEntityStatusTag } from "../bid/contributing/ContributingEntityStatusTag";
import { ContributionWizard } from "./ContributionWizard";
import { useContributionUrls } from "./useContributionUrls";
import { useDebouncedEspdSave, useDebouncedSave } from "./useDebouncedEspdSave";

interface Props {
  entityId: string;
  detail: ContributingEntityDetailDto;
}

export const ContributingEntityForm = ({ entityId, detail }: Props) => {
  const { t } = useTranslation();
  const displayer = useApiError();
  const toast = useToast();
  const contributionUrls = useContributionUrls();

  const [updateResponse] = useUpdateContributingEntityResponseMutation();
  const [updateQualifications] = useUpdateContributingEntityQualificationResponsesMutation();
  const [updateCommitment] = useUpdateContributingEntityCommitmentMutation();
  const [submitResponse, { isLoading: isSubmitting }] = useSubmitContributingEntityResponseMutation();

  const [espd, setEspd] = useState<EspdResponseDto>(detail.espdResponse ?? emptyEspd());
  const [qualificationResponses, setQualificationResponses] = useState<
    ContributingEntityQualificationResponseDto[]
  >(detail.qualificationResponses ?? []);
  const [commitment, setCommitment] = useState<ContributingEntityCommitmentDto>(
    detail.commitment ?? { confirmed: false },
  );

  const { save, isSaving, settle: settleEspd } = useDebouncedEspdSave(async (next) => {
    await updateResponse({
      entityId,
      updateContributingEntityResponseRequestDto: { espdResponse: next },
    }).unwrap();
  });
  const {
    save: saveQualifications,
    isSaving: isSavingQualifications,
    settle: settleQualifications,
  } = useDebouncedSave<
    ContributingEntityQualificationResponseDto[]
  >(async (next) => {
    await updateQualifications({
      entityId,
      updateContributingEntityQualificationResponsesRequestDto: { responses: next },
    }).unwrap();
  });
  const {
    save: saveCommitment,
    isSaving: isSavingCommitment,
    settle: settleCommitment,
  } = useDebouncedSave<
    ContributingEntityCommitmentDto
  >(async (next) => {
    await updateCommitment({
      entityId,
      updateContributingEntityCommitmentRequestDto: { commitment: next },
    }).unwrap();
  });

  const readOnly = !detail.responseEditable;

  const change = (next: EspdResponseDto) => {
    setEspd(next);
    if (!readOnly) save(next);
  };
  const changeQualifications = (next: ContributingEntityQualificationResponseDto[]) => {
    setQualificationResponses(next);
    if (!readOnly) saveQualifications(next);
  };
  const changeCommitment = (next: ContributingEntityCommitmentDto) => {
    setCommitment(next);
    if (!readOnly) saveCommitment(next);
  };

  const isSavingAnything = isSaving || isSavingQualifications || isSavingCommitment;

  const submit = async () => {
    try {
      const saved = await Promise.all([settleEspd(), settleQualifications(), settleCommitment()]);
      if (saved.some((ok) => !ok)) {
        toast({
          title: t("Your answer was not submitted because it could not be saved. Try again."),
          status: "error",
        });
        return;
      }
      await submitResponse({ entityId }).unwrap();
    } catch (e) {
      displayer.trigger(e);
    }
  };

  return (
    <ContributionWizard
      detail={detail}
      espd={espd}
      qualificationResponses={qualificationResponses}
      commitment={commitment}
      onEspdChange={change}
      onQualificationsChange={changeQualifications}
      onCommitmentChange={changeCommitment}
      readOnly={readOnly}
      audience="contributor"
      stepPaths={contributionUrls.steps(entityId)}
      backTo={contributionUrls.list}
      backLabel={t("Back to my contributions")}
      title={t("Your self-declaration")}
      headingAside={
        <>
          <ContributingEntityStatusTag status={detail.entity.status} />
          {isSavingAnything && <Spinner size="sm" color="smMuted" />}
        </>
      }
      isSaving={isSavingAnything}
      isSubmitting={isSubmitting}
      onSubmit={submit}
    />
  );
};
